/**
 * recorderStore — lưu dữ liệu ghi âm/ghi màn hình TRỰC TIẾP xuống IndexedDB
 * theo từng chunk (append), thay vì giữ toàn bộ trong RAM.
 *
 * Mục tiêu: KHÔNG mất file kể cả khi tab bị trình duyệt discard/kill (hết RAM,
 * chạy nền quá lâu). Mỗi chunk MediaRecorder tạo ra được ghi ngay xuống đĩa;
 * lần mở app kế tiếp có thể ghép lại thành file và cho người dùng tải về.
 *
 * Chỉ 1 phiên ghi tại một thời điểm → dùng 1 key 'active' cho metadata.
 */

const DB_NAME = 'media-recorder-db'
const DB_VERSION = 2
const META_STORE = 'session'
const CHUNK_STORE = 'chunks'
const AUDIO_CHUNK_STORE = 'chunks_audio'
const META_KEY = 'active'

/**
 * Ghi màn hình tạo ra hai luồng song song:
 *   'main'  — video + tiếng, để người dùng tải về
 *   'audio' — chỉ tiếng, để gửi lên server bóc băng
 * Engine chỉ nghe tiếng, còn phần hình chiếm ~95% dung lượng, nên tách ra giúp
 * bản upload nhẹ đi khoảng 19 lần mà người dùng vẫn tải được video đầy đủ.
 * Ghi âm mic chỉ có một luồng và luôn dùng 'main'.
 */
export type ChunkKind = 'main' | 'audio'

const storeOf = (kind: ChunkKind) => (kind === 'audio' ? AUDIO_CHUNK_STORE : CHUNK_STORE)

export interface RecorderSessionMeta {
    type: 'mic' | 'screen'
    mimeType: string
    filename: string
    startedAt: number
    status: 'recording' | 'stopped'
    durationSec?: number
    /** Chỉ có khi phiên ghi kèm luồng audio riêng để upload. */
    audioMimeType?: string
    audioFilename?: string
}

let dbPromise: Promise<IDBDatabase> | null = null

function openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION)
        req.onupgradeneeded = () => {
            const db = req.result
            if (!db.objectStoreNames.contains(META_STORE)) {
                db.createObjectStore(META_STORE)
            }
            if (!db.objectStoreNames.contains(CHUNK_STORE)) {
                // autoIncrement: key tăng dần = đúng thứ tự chèn = thứ tự chunk
                db.createObjectStore(CHUNK_STORE, {autoIncrement: true})
            }
            if (!db.objectStoreNames.contains(AUDIO_CHUNK_STORE)) {
                db.createObjectStore(AUDIO_CHUNK_STORE, {autoIncrement: true})
            }
        }
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
    })
}

function db(): Promise<IDBDatabase> {
    if (!dbPromise) dbPromise = openDB()
    return dbPromise
}

/** Bắt đầu phiên mới: xoá chunk cũ + ghi metadata. */
export async function resetSession(meta: RecorderSessionMeta): Promise<void> {
    const conn = await db()
    await new Promise<void>((resolve, reject) => {
        const t = conn.transaction([META_STORE, CHUNK_STORE, AUDIO_CHUNK_STORE], 'readwrite')
        t.objectStore(CHUNK_STORE).clear()
        t.objectStore(AUDIO_CHUNK_STORE).clear()
        t.objectStore(META_STORE).put(meta, META_KEY)
        t.oncomplete = () => resolve()
        t.onerror = () => reject(t.error)
    })
}

/** Ghi 1 chunk (Blob) xuống đĩa. Blob lưu trực tiếp — trình duyệt có thể spill ra ổ cứng. */
export async function appendChunk(blob: Blob, kind: ChunkKind = 'main'): Promise<void> {
    if (!blob || blob.size === 0) return
    const conn = await db()
    await new Promise<void>((resolve, reject) => {
        const t = conn.transaction(storeOf(kind), 'readwrite')
        t.objectStore(storeOf(kind)).add(blob)
        t.oncomplete = () => resolve()
        t.onerror = () => reject(t.error)
    })
}

/** Cập nhật một phần metadata (vd status, durationSec). */
export async function patchSession(patch: Partial<RecorderSessionMeta>): Promise<void> {
    const conn = await db()
    await new Promise<void>((resolve, reject) => {
        const t = conn.transaction(META_STORE, 'readwrite')
        const store = t.objectStore(META_STORE)
        const getReq = store.get(META_KEY)
        getReq.onsuccess = () => {
            const cur = getReq.result
            if (cur) store.put({...cur, ...patch}, META_KEY)
        }
        t.oncomplete = () => resolve()
        t.onerror = () => reject(t.error)
    })
}

export async function getSessionMeta(): Promise<RecorderSessionMeta | null> {
    const conn = await db()
    return new Promise((resolve) => {
        const req = conn.transaction(META_STORE, 'readonly').objectStore(META_STORE).get(META_KEY)
        req.onsuccess = () => resolve((req.result as RecorderSessionMeta) ?? null)
        req.onerror = () => resolve(null)
    })
}

export async function getChunkCount(kind: ChunkKind = 'main'): Promise<number> {
    const conn = await db()
    return new Promise((resolve) => {
        const req = conn.transaction(storeOf(kind), 'readonly').objectStore(storeOf(kind)).count()
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => resolve(0)
    })
}

/** Ghép tất cả chunk đã lưu thành 1 Blob theo đúng thứ tự. */
export async function assembleBlob(mimeType: string, kind: ChunkKind = 'main'): Promise<Blob> {
    const conn = await db()
    const parts: Blob[] = await new Promise((resolve) => {
        const req = conn.transaction(storeOf(kind), 'readonly').objectStore(storeOf(kind)).getAll()
        req.onsuccess = () => resolve((req.result as Blob[]) || [])
        req.onerror = () => resolve([])
    })
    return new Blob(parts, {type: mimeType})
}

/** Xoá sạch phiên (metadata + chunk). */
export async function clearSession(): Promise<void> {
    const conn = await db()
    await new Promise<void>((resolve) => {
        const t = conn.transaction([META_STORE, CHUNK_STORE, AUDIO_CHUNK_STORE], 'readwrite')
        t.objectStore(CHUNK_STORE).clear()
        t.objectStore(AUDIO_CHUNK_STORE).clear()
        t.objectStore(META_STORE).delete(META_KEY)
        t.oncomplete = () => resolve()
        t.onerror = () => resolve()
    })
}
